import React, { useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import ChatShell from './ChatShell';

/**
 * Floating widget. Same backend, same knowledge file, same trust tags as
 * the full-page chat. Port commitment: ≤2 days from "go" signal to live.
 */

export default function BubbleShell({ enabled = false }) {
  const [open, setOpen] = useState(false);

  if (!enabled) return null;

  return (
    <div data-drishti="bubble-shell" className="pointer-events-none fixed bottom-5 right-5 z-50">
      {open ? (
        <div className="pointer-events-auto flex h-[600px] w-[400px] flex-col overflow-hidden rounded-3xl border border-app-border/80 bg-white shadow-[0_24px_60px_rgba(6,43,85,0.28)] ring-1 ring-black/5">
          {/* Header — subtle gradient, status dot, type hierarchy */}
          <div
            className="flex items-center justify-between px-5 py-3 text-white"
            style={{ background: 'linear-gradient(135deg, #041f3d 0%, #062B55 60%, #0a3768 100%)' }}
          >
            <div className="flex items-center gap-2.5">
              <span
                className="flex h-7 w-7 items-center justify-center rounded-full"
                style={{ background: 'rgba(255,255,255,0.12)' }}
              >
                <Sparkles className="h-3.5 w-3.5 text-white" strokeWidth={2.2} />
              </span>
              <div className="leading-tight">
                <div className="flex items-center gap-1.5 text-[13px] font-semibold tracking-[-0.01em]">
                  Drishti
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400"
                    aria-hidden="true"
                    title="online"
                  />
                </div>
                <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-white/55">
                  AI analyst
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close Drishti"
              className="rounded-lg p-1.5 text-white/70 transition hover:bg-white/10 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-hidden bg-gradient-to-b from-white to-slate-50/60">
            <ChatShell layout="bubble" />
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open Drishti"
          className="drishti-launcher pointer-events-auto group relative flex h-14 w-14 items-center justify-center rounded-full text-white transition hover:scale-[1.04] active:scale-95"
          style={{
            background: 'linear-gradient(135deg, #062B55 0%, #0a3768 55%, #6FA8F7 120%)',
            boxShadow: '0 10px 28px rgba(6,43,85,0.35), 0 0 0 1px rgba(111,168,247,0.35)',
          }}
        >
          {/* Breath ring — subtle, single slow pulse */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
              boxShadow: '0 0 0 0 rgba(111,168,247,0.45)',
              animation: 'drishti-breath 2.8s ease-out infinite',
            }}
          />
          <Sparkles className="h-5 w-5 relative" strokeWidth={2.2} />
        </button>
      )}

      {/* Keyframes for the breath ring. Co-located with the component so
          deleting the widget removes the styles automatically. */}
      <style>{`
        @keyframes drishti-breath {
          0%   { box-shadow: 0 0 0 0 rgba(111,168,247,0.45); }
          70%  { box-shadow: 0 0 0 14px rgba(111,168,247,0); }
          100% { box-shadow: 0 0 0 0 rgba(111,168,247,0); }
        }
      `}</style>
    </div>
  );
}
